const features = [
  {
    id: 'encryption',
    icon: 'ri-lock-2-line',
    title: 'Military-Grade Encryption',
    description:
      'AES-256 encryption protects every byte of your data in transit. Our VPN uses the same security standards trusted by governments and financial institutions worldwide.',
    image:
      'https://public.readdy.ai/ai/img_res/b2e19033f0c8a50ec47380e4fecdffe2.jpg',
  },
  {
    id: 'performance',
    icon: 'ri-speed-up-line',
    title: 'One-Tap Optimization',
    description:
      'Instantly reclaim gigabytes of storage and boost device performance with a single tap. Smart Cleaner intelligently identifies and removes unnecessary files without touching your important data.',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20speed%20and%20performance%20concept%20art%2C%20glowing%20lightning%20bolt%20with%20dynamic%20motion%20lines%2C%20warm%20orange%20amber%20gradient%20on%20dark%20background%2C%20energy%20and%20power%20visualization%2C%20no%20text%2C%20high%20resolution%20minimalist%20digital%20art&width=600&height=600&seq=feat002&orientation=squarish',
  },
  {
    id: 'privacy',
    icon: 'ri-eye-off-line',
    title: 'Privacy First',
    description:
      'We operate a strict zero-log policy. Your browsing history, connection timestamps, and IP addresses are never recorded, stored, or shared with any third party — ever.',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20privacy%20and%20anonymity%20concept%20art%2C%20eye%20with%20a%20slash%20surrounded%20by%20soft%20glowing%20particles%2C%20deep%20purple%20indigo%20dark%20background%2C%20digital%20privacy%20visualization%2C%20no%20text%2C%20high%20resolution%20minimalist%20digital%20art&width=600&height=600&seq=feat003&orientation=squarish',
  },
  {
    id: 'interface',
    icon: 'ri-smartphone-line',
    title: 'Native iOS Experience',
    description:
      'Built exclusively for iOS using native frameworks, our apps integrate seamlessly with the Apple ecosystem — supporting Face ID, Siri Shortcuts, and iOS widgets for effortless daily use.',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20iOS%20native%20app%20interface%20concept%20art%2C%20clean%20minimal%20phone%20silhouette%20with%20glowing%20UI%20elements%2C%20soft%20silver%20white%20gradient%20on%20light%20gray%20background%2C%20Apple%20design%20language%20aesthetic%2C%20no%20text%2C%20high%20resolution%20minimalist%20digital%20art&width=600&height=600&seq=feat004&orientation=squarish',
  },
  {
    id: 'updates',
    icon: 'ri-refresh-line',
    title: 'Continuous Updates',
    description:
      'Our dedicated engineering team ships regular updates to stay ahead of emerging threats and iOS changes. You always have the most secure and optimized version of our apps.',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20software%20update%20and%20continuous%20improvement%20concept%20art%2C%20circular%20arrows%20with%20glowing%20nodes%20and%20connection%20lines%2C%20cool%20blue%20teal%20gradient%20on%20dark%20background%2C%20technology%20evolution%20visualization%2C%20no%20text%2C%20high%20resolution%20minimalist%20digital%20art&width=600&height=600&seq=feat005&orientation=squarish',
  },
  {
    id: 'support',
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Support',
    description:
      'Our expert support team is available to help you get the most out of your apps. From setup guidance to troubleshooting, we are committed to your satisfaction.',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20customer%20support%20and%20help%20concept%20art%2C%20headset%20icon%20with%20soft%20glowing%20communication%20waves%2C%20warm%20coral%20rose%20gradient%20on%20dark%20background%2C%20assistance%20and%20care%20visualization%2C%20no%20text%2C%20high%20resolution%20minimalist%20digital%20art&width=600&height=600&seq=feat006&orientation=squarish',
  },
];

export default function FeaturesSection() {
  return (
    <section id="features" className="w-full bg-white py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16 md:mb-24">
          <div className="inline-flex items-center border border-gray-300 rounded-full px-5 py-1.5 mb-8">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
              Key Capabilities
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Why Choose COMPANY
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-500 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Every feature is engineered with one goal: giving iOS users the best possible experience in security and performance.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="group relative rounded-2xl overflow-hidden aspect-square"
            >
              {/* Background Image */}
              <img
                src={feature.image}
                alt={feature.title}
                className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm mb-4">
                  <i className={`${feature.icon} text-white text-xl`} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white mb-2 leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-sm text-white/70 leading-relaxed line-clamp-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
